import { Avatar, Button, Col, Progress, Row } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineAudio, AiOutlineAudioMuted } from "react-icons/ai";
import { FaVideo, FaVideoSlash } from "react-icons/fa6";
import { MdCallEnd } from "react-icons/md";
import callingMusic from "../../../assets/waiting-music.mp3";
import { useCallingContext } from "../../../context/CallingContext";
import { useInfoUser } from "../../../store/UserStore";
import { getAvatar } from "../../../utils/Utils";

const InCalling = () => {
  const { languageMap } = useInfoUser();

  const {
    friend,
    localSrc,
    peerSrc,
    endCall,
    toggleAudio,
    toggleVideo,
    me,
    isHost,
    isClosed,
    callingStatus,
  } = useCallingContext();

  const [percent, setPercent] = useState(0);

  const peerVideo = useRef(null);
  const localVideo = useRef(null);
  const timerRef = useRef(null);

  const callingProblem = useMemo(() => {
    switch (callingStatus) {
      case "offline":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            <Avatar
              size={100}
              src={friend?.avatar ? getAvatar(friend) : null}
              className="bg-[#ffc0bb]"
            >
              <div className="text-[white] text-[50px]">
                {friend?.name?.[0] || friend?.username?.[0]}
              </div>
            </Avatar>
            <div className="text-[24px] font-semibold">{friend?.name}</div>
            <div className="text-[24px]">
              {languageMap?.["call.offline"] ?? "is offline"}
            </div>
          </div>
        );
      case "in-calling":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            <Avatar
              size={100}
              src={friend?.avatar ? getAvatar(friend) : null}
              className="bg-[#ffc0bb]"
            >
              <div className="text-[white] text-[50px]">
                {friend?.name?.[0] || friend?.username?.[0]}
              </div>
            </Avatar>
            <div className="text-[24px] font-semibold">{friend?.name}</div>
            <div className="text-[24px]">
              {languageMap?.["call.other"] ?? "You are in other call"}
            </div>
          </div>
        );
      case "not-exist":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            <Avatar
              size={100}
              src={friend?.avatar ? getAvatar(friend) : null}
              className="bg-[#ffc0bb]"
            >
              <div className="text-[white] text-[50px]">
                {friend?.name?.[0] || friend?.username?.[0]}
              </div>
            </Avatar>
            <div className="text-[24px] font-semibold">{friend?.name}</div>
            <div className="text-[24px]">
              {languageMap?.["call.inactive"] ?? "This call has been inactive"}
            </div>
          </div>
        );
      case "busy":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            <Avatar
              size={100}
              src={friend?.avatar ? getAvatar(friend) : null}
              className="bg-[#ffc0bb]"
            >
              <div className="text-[white] text-[50px]">
                {friend?.name?.[0] || friend?.username?.[0]}
              </div>
            </Avatar>
            <div className="text-[24px] font-semibold">{friend?.name}</div>
            <div className="text-[24px]">
              {languageMap?.["call.busy"] ?? "in other call"}
            </div>
          </div>
        );
      case "audio":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            Your microphone had problem
          </div>
        );
      case "video":
        return (
          <div className="text-[white] flex justify-center items-center flex-col h-full">
            Your camera had problem
          </div>
        );
      default:
        break;
    }
  }, [callingStatus]);

  useEffect(() => {
    if (localVideo.current && localSrc) {
      localVideo.current.srcObject = localSrc;
    }
  }, [localSrc]);

  useEffect(() => {
    if (peerVideo.current && peerSrc) {
      clearInterval(timerRef.current);
      peerVideo.current.srcObject = peerSrc;
    }
  }, [peerSrc]);

  useEffect(() => {
    const totalTime = 70 * 1000; // 1m27s
    const interval = 1000;
    const increment = 100 / (totalTime / interval);

    timerRef.current = setInterval(() => {
      if (peerSrc) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }

      setPercent((prev) => {
        if (prev >= 100) {
          clearInterval(timerRef.current);
          window.close();
          return 100;
        }
        return prev + increment;
      });
    }, interval);

    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isClosed) window.close();
  }, [isClosed]);

  return (
    <>
      {[
        "closed",
        "in-calling",
        "not-exist",
        "offline",
        "busy",
        "video",
        "audio",
      ]?.includes(callingStatus) ? (
        callingProblem
      ) : (
        <Row className="h-[100%] bg-[black]">
          <Col span={8} className="flex flex-col justify-end pb-[25px] ">
            <div className={`flex gap-[10px] ml-[20px]`}>
              <div className="text-white">{friend?.name}</div>
              <AiOutlineAudioMuted
                color="red"
                size={20}
                className={`${friend?.audio && "hidden"}`}
              />
            </div>
          </Col>
          <Col
            span={8}
            className="flex flex-col items-center justify-between py-[40px]"
          >
            {!peerSrc && isHost ? (
              <div className="flex flex-col items-center justify-between pt-[100px] text-[white]">
                <audio src={callingMusic} preload="auto" autoPlay />
                <Progress
                  type="circle"
                  percent={percent}
                  strokeColor="#ffffff"
                  format={() => (
                    <div>
                      <Avatar
                        size={100}
                        src={friend?.avatar ? getAvatar(friend) : null}
                        className="text-[white]"
                      >
                        <div className="text-[50px]">
                          {friend?.name?.[0] || friend?.username?.[0]}
                        </div>
                      </Avatar>
                    </div>
                  )}
                  className="!text-[white]"
                />
                <div className="text-[24px] font-semibold">{friend?.name}</div>
                <div className="text-[20px] text-[#cbc1c1]">
                  {languageMap?.["call.incoming"] ?? "is calling "}...
                </div>
              </div>
            ) : (
              <>
                <div
                  className={`w-[700px] flex flex-col items-center gap-[10px] ${!friend?.video && "hidden"}`}
                >
                  <video
                    className="rounded-[5px]"
                    ref={peerVideo}
                    autoPlay
                    // muted
                  />
                </div>

                <div
                  className={`flex flex-col items-center gap-[10px] mt-[150px] ${friend?.video && "hidden"}`}
                >
                  <Avatar
                    size={150}
                    src={friend?.avatar ? getAvatar(friend) : null}
                    className="text-[white]"
                  >
                    <div className="text-[50px]">
                      {friend?.name?.[0] || friend?.username?.[0]}
                    </div>
                  </Avatar>
                </div>
              </>
            )}
            <div className="flex gap-[10px]">
              {peerSrc && (
                <>
                  {me?.video ? (
                    <Button
                      type="default"
                      shape="circle"
                      className="bg-[#2a56b9] text-[white]"
                      onClick={() => {
                        toggleVideo(false);
                      }}
                      icon={<FaVideo className="text-[18px]" size={25} />}
                      size="large"
                    />
                  ) : (
                    <Button
                      type="default"
                      shape="circle"
                      className="bg-[#ef4444] text-[white]"
                      onClick={() => {
                        toggleVideo(true);
                      }}
                      icon={<FaVideoSlash className="text-[18px]" size={25} />}
                      size="large"
                    />
                  )}
                  {me?.audio ? (
                    <Button
                      type="default"
                      shape="circle"
                      className="bg-[#2a56b9] text-[white]"
                      onClick={() => {
                        toggleAudio(false);
                      }}
                      icon={
                        <AiOutlineAudio className="text-[18px]" size={25} />
                      }
                      size="large"
                    />
                  ) : (
                    <Button
                      type="default"
                      shape="circle"
                      className="bg-[#ef4444] text-[white]"
                      onClick={() => {
                        toggleAudio(true);
                      }}
                      icon={
                        <AiOutlineAudioMuted
                          className="text-[18px]"
                          size={25}
                        />
                      }
                      size="large"
                    />
                  )}
                </>
              )}
              <Button
                type="default"
                shape="circle"
                className="bg-[red] text-[white]"
                onClick={() => endCall(true)}
                icon={<MdCallEnd className="text-[18px]" size={25} />}
                size="large"
              />
            </div>
          </Col>

          <Col
            span={8}
            className="flex flex-col justify-end pb-[25px] items-end"
          >
            <div className="w-[200px] h-[150px] mr-[20px]">
              <video
                className="rounded-[5px]"
                // id="localVideo"
                ref={localVideo}
                autoPlay
                muted
              />
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export { InCalling };
